import React, { useState, useEffect } from 'react';
import { apiClient, uploadFile } from '../api/client';

const VALID_PAGE_KEYS = [
  { key: 'guest-toolkit', label: 'Guest Toolkit' },
  { key: 'marketplace', label: 'Marketplace' },
  { key: 'chapters', label: 'Chapters' },
  { key: 'fun-facts', label: 'Fun Facts' },
];

interface ThemeConfig {
  globalDefaultUrl: string | null;
  overrides: Record<string, string>;
}

const iconBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 6px',
  fontSize: '0.9rem',
  borderRadius: '4px',
  lineHeight: 1,
  transition: 'background-color 0.15s',
};

export function ThemeAdminPanel() {
  const [config, setConfig] = useState<ThemeConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient<ThemeConfig>('/theme').then(setConfig).catch(() => {});
  }, []);

  async function handleGlobalUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const result = await uploadFile<{ globalDefaultUrl: string }>(
        '/theme/global-background',
        file,
        'file',
      );
      setConfig((prev) =>
        prev ? { ...prev, globalDefaultUrl: result.globalDefaultUrl } : prev,
      );
    } catch (err: any) {
      setError(
        err?.message || 'Upload failed. Accepted formats: JPEG, PNG, WebP.',
      );
    }
  }

  async function handleOverrideUpload(
    pageKey: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const result = await uploadFile<{ imageUrl: string }>(
        `/theme/page-override/${pageKey}`,
        file,
        'file',
      );
      setConfig((prev) =>
        prev
          ? { ...prev, overrides: { ...prev.overrides, [pageKey]: result.imageUrl } }
          : prev,
      );
    } catch (err: any) {
      setError(err?.message || 'Upload failed.');
    }
  }

  async function handleRemoveOverride(pageKey: string) {
    try {
      await apiClient(`/theme/page-override/${pageKey}`, { method: 'DELETE' });
      setConfig((prev) => {
        if (!prev) return prev;
        const { [pageKey]: _, ...rest } = prev.overrides;
        return { ...prev, overrides: rest };
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to remove override.');
    }
  }

  if (!config) return <p>Loading theme config...</p>;

  return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ margin: '0 0 0.5rem' }}>Global Default Background</h4>
        {config.globalDefaultUrl && (
          <img
            src={config.globalDefaultUrl}
            alt="Global background preview"
            style={{
              width: 120,
              height: 80,
              objectFit: 'cover',
              borderRadius: 8,
              marginBottom: 8,
              display: 'block',
            }}
          />
        )}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleGlobalUpload}
        />
      </div>

      <h4 style={{ margin: '0 0 0.5rem' }}>Per-Page Overrides</h4>
      {VALID_PAGE_KEYS.map(({ key, label }) => (
        <div
          key={key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '0.75rem',
          }}
        >
          <span style={{ minWidth: 120 }}>{label}</span>
          {config.overrides[key] ? (
            <>
              <img
                src={config.overrides[key]}
                alt={`${label} override`}
                style={{
                  width: 60,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 4,
                }}
              />
              <button
                style={iconBtn}
                onClick={() => handleRemoveOverride(key)}
                title={`Remove override for ${label}`}
                aria-label={`Remove override for ${label}`}
              >
                ✕ Remove
              </button>
            </>
          ) : (
            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
              Using global default
            </span>
          )}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => handleOverrideUpload(key, e)}
          />
        </div>
      ))}

      {error && (
        <p style={{ color: '#d32f2f', marginTop: '0.75rem' }}>{error}</p>
      )}
    </div>
  );
}
